import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SubPageLayout from "../../components/shared/SubPageLayout";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { motion, AnimatePresence } from "framer-motion";
import Modal from "../../components/shared/Modal";

import logger from '../../utils/logger';

// Base styles for form inputs
const baseInputStyles = "w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 placeholder-gray-400 dark:placeholder-gray-500 hover:border-blue-500 dark:hover:border-blue-400 transition-colors";

// Define styles object at the top level, before any references to it
const styles = {
  dropTarget: {
    border: "2px dashed #e5e7eb",
    transition: "all 0.3s ease",
    backgroundColor: "#f9fafb",
    borderRadius: "0.75rem",
  },
  dropTargetActive: {
    border: "2px dashed #3b82f6",
    backgroundColor: "rgba(59, 130, 246, 0.05)",
  },
  elementTransition: {
    transition: "all 0.3s ease",
  },
  fadeIn: {
    animation: "fadeIn 0.3s ease-out",
  },
  slideIn: {
    animation: "slideIn 0.3s ease-out",
  },
  shake: {
    animation: "shake 0.5s ease-in-out",
  },
  keyframes: `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideIn {
      from { transform: translateY(-20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }
    @keyframes slide-in-right {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes scale-in {
      from { transform: scale(0.95); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
  `,
  typography: {
    h1: "text-3xl font-bold text-gray-900 dark:text-gray-100",
    h2: "text-2xl font-semibold text-gray-900 dark:text-gray-100",
    h3: "text-xl font-semibold text-gray-900 dark:text-gray-100",
    h4: "text-lg font-medium text-gray-900 dark:text-gray-100",
    body: "text-base text-gray-700 dark:text-gray-300",
    small: "text-sm text-gray-600 dark:text-gray-400",
  },
  shadows: {
    sm: "shadow-sm hover:shadow transition-shadow duration-200",
    md: "shadow-md dark:shadow-none hover:shadow-lg dark:shadow-none transition-shadow duration-200",
    lg: "shadow-lg dark:shadow-none hover:shadow-xl transition-shadow duration-200",
  },
  buttons: {
    primary:
      "px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
    secondary:
      "px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2",
    danger:
      "px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
    success:
      "px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2",
    icon: "p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2",
  },
  inputs: {
    base: "w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-400 transition-colors duration-200",
    error: "border-red-500 focus:border-red-500 focus:ring-red-200",
    success: "border-green-500 focus:border-green-500 focus:ring-green-200",
  },
  cards: {
    base: "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700",
    hover: "hover:border-blue-500 hover:shadow-md dark:shadow-none transition-all duration-200",
    selected: "ring-2 ring-blue-500 shadow-md dark:shadow-none",
  },
  notifications: {
    container: "fixed bottom-4 right-4 z-50 space-y-2",
    toast: `
      flex items-center p-4 rounded-lg shadow-lg dark:shadow-none transform transition-all duration-300
      animate-slide-in-right bg-white dark:bg-gray-800 border-l-4
    `,
    success: "border-green-500 bg-green-50",
    error: "border-red-500 bg-red-50",
    warning: "border-yellow-500 bg-yellow-50",
    info: "border-blue-500 bg-blue-50",
    icon: "flex-shrink-0 w-5 h-5 mr-3",
    content: "flex-1",
    title: "font-medium",
    message: "text-sm text-gray-600 dark:text-gray-400",
    closeButton: "ml-4 text-gray-400 hover:text-gray-600 dark:text-gray-400 focus:outline-none",
  },
  popup: {
    overlay:
      "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50",
    container: `
      bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all duration-300
      max-w-lg w-full mx-4 animate-scale-in
    `,
    header: "flex items-center justify-between p-4 border-b",
    title: "text-lg font-semibold text-gray-900 dark:text-gray-100",
    content: "p-6",
    footer: "flex justify-end space-x-3 p-4 bg-gray-50 rounded-b-lg",
  },
  sidebar: {
    search: `${baseInputStyles} mb-4`,
    category: {
      header: `
        flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-800
        rounded-lg cursor-pointer transition-colors duration-200
      `,
      icon: "w-5 h-5 text-gray-400 transform transition-transform duration-200",
      content: "overflow-hidden transition-all duration-200",
    },
  },
};

// Add a style tag for keyframes
const KeyframesStyle = () => (
  <style>
    {`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideIn {
        from { transform: translateY(-20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
      }
      @keyframes slide-in-right {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes scale-in {
        from { transform: scale(0.95); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      .toggle-checkbox:checked {
        right: 0;
        border-color: #3b82f6;
      }
      .toggle-checkbox:checked + .toggle-label {
        background-color: #3b82f6;
      }
      .toggle-label {
        transition: background-color 0.2s ease;
      }
    `}
  </style>
);

// Add this helper function at the top level
const evaluateCondition = (condition, formElements) => {
  if (!condition || !condition.fieldId || !condition.operator) return true;

  const targetField = formElements.find((el) => el.id === condition.fieldId);
  if (!targetField) return true;

  const value = targetField.value;

  // Handle undefined/null values
  if (value === undefined || value === null) return false;

  switch (condition.operator) {
    case "equals":
      return (
        String(value).toLowerCase() === String(condition.value).toLowerCase()
      );
    case "not_equals":
      return (
        String(value).toLowerCase() !== String(condition.value).toLowerCase()
      );
    case "contains":
      return String(value)
        .toLowerCase()
        .includes(String(condition.value).toLowerCase());
    case "not_contains":
      return !String(value)
        .toLowerCase()
        .includes(String(condition.value).toLowerCase());
    case "is_empty":
      return !value;
    case "is_not_empty":
      return !!value;
    case "is_checked":
      return value === true;
    default:
      return false; // Changed from true to false for safety
  }
};

// Form Elements Components
const FormElement = ({
  id,
  type,
  label,
  config,
  onConfigChange,
  preview = false,
  formElements = [],
  onChange,
}) => {
  const [selectedValue, setSelectedValue] = useState(
    preview ? config.value : null,
  );

  useEffect(() => {
    if (preview) {
      setSelectedValue(config.value);
    }
  }, [preview, config.value]);

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  const handleChange = (e, fieldIndex) => {
    if (config.isComposite) {
      const newValues = { ...(selectedValue || {}) };
      newValues[config.fields[fieldIndex].dbField] = e.target.value;
      setSelectedValue(newValues);
      onChange?.(newValues);
    } else if (config.multiField && typeof fieldIndex === "number") {
      const newValues = { ...(selectedValue || {}) };
      newValues[fieldIndex] = e.target.value;
      setSelectedValue(newValues);
      onChange?.(newValues);
    } else {
      const value =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setSelectedValue(value);
      onChange?.(value);
    }
  };

  // If in preview mode and conditions are not met, don't render
  if (preview && config.conditional) {
    const shouldShow = evaluateCondition(config.conditions, formElements);
    if (!shouldShow) return null;
  }

  // Don't render page separators in preview mode
  if (preview && type === "page-separator") {
    return null;
  }

  const renderElement = () => {
    if (type === "form-name") {
      return (
        <div className="mb-4">
          <input
            type="text"
            value={config.value || ""}
            onChange={(e) =>
              onConfigChange({ ...config, value: e.target.value })
            }
            placeholder="Enter Form Name"
            className={`w-full p-2 border rounded-lg text-xl font-semibold ${
              preview ? "border-transparent bg-transparent" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            }`}
          />
        </div>
      );
    }

    if (type === "section-name") {
      // Check if this section name should be locked (no page breaks exist)
      const pageBreakExists = formElements.some(
        (el) => el.type === "page-separator",
      );
      const isFirstSection = !formElements
        .slice(
          0,
          formElements.findIndex((el) => el.id === id),
        )
        .some((el) => el.type === "page-separator");
      const isLocked = isFirstSection && !pageBreakExists;

      return (
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={config.value || ""}
              onChange={(e) =>
                onConfigChange({ ...config, value: e.target.value })
              }
              placeholder="Enter Section Name"
              disabled={isLocked}
              className={`w-full p-2 border rounded-lg text-lg ${
                preview
                  ? "border-transparent bg-transparent"
                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              } ${isLocked ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed" : ""}`}
            />
            {!preview && (
              <button
                onClick={() =>
                  onConfigChange({ ...config, hidden: !config.hidden })
                }
                className="p-2 text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
                title={
                  config.hidden ? "Show section name" : "Hide section name"
                }
              >
                {config.hidden ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      );
    }

    switch (type) {
      case "text":
        if (config.isComposite) {
          return (
            <div className="flex gap-4">
              {config.fields?.map((field, index) => (
                <div key={field.dbField} className="flex-1">
                  <label className="block text-sm text-gray-900 dark:text-gray-100 mb-1">
                    {field.label}
                  </label>
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    value={selectedValue?.[field.dbField] || ""}
                    className={baseInputStyles}
                    required={field.required}
                    onClick={handleInputClick}
                    onChange={(e) => handleChange(e, index)}
                    style={{
                      fontSize: config.fontSize || "1rem",
                      fontFamily: config.fontFamily || "inherit",
                    }}
                  />
                  {field.required && (
                    <span className="text-red-500 text-xs ml-1">*</span>
                  )}
                </div>
              ))}
            </div>
          );
        }
        if (config.multiField) {
          return (
            <div className="flex gap-4">
              {config.fields?.map((field, index) => (
                <div key={index} className="flex-1">
                  <label className="block text-sm text-gray-900 dark:text-gray-100 mb-1">
                    {field.label}
                  </label>
                  <input
                    type="text"
                    placeholder={field.placeholder || ""}
                    value={selectedValue?.[index] || ""}
                    className={baseInputStyles}
                    required={field.required}
                    onClick={handleInputClick}
                    onChange={(e) => handleChange(e, index)}
                    style={{
                      fontSize: config.fontSize || "1rem",
                      fontFamily: config.fontFamily || "inherit",
                    }}
                  />
                  {field.required && (
                    <span className="text-red-500 text-xs ml-1">*</span>
                  )}
                </div>
              ))}
            </div>
          );
        }
        return (
          <input
            type="text"
            placeholder={config.placeholder || ""}
            className={baseInputStyles}
            required={config.required}
            onClick={handleInputClick}
            onChange={(e) => handleChange(e)}
            style={{
              fontSize: config.fontSize || "1rem",
              fontFamily: config.fontFamily || "inherit",
            }}
          />
        );
      case "dropdown":
        return (
          <select
            className={baseInputStyles}
            required={config.required}
            onClick={handleInputClick}
            onChange={(e) => handleChange(e)}
            style={{
              fontSize: config.fontSize || "1rem",
              fontFamily: config.fontFamily || "inherit",
            }}
          >
            <option value="" className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default">
              Select...
            </option>
            {config.options?.map((option, index) => (
              <option
                key={index}
                value={option.value}
                className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
              >
                {option.label}
              </option>
            ))}
          </select>
        );
      case "multiselect":
        return (
          <select
            multiple
            className={baseInputStyles}
            required={config.required}
            onClick={handleInputClick}
            onChange={(e) => handleChange(e)}
            style={{
              fontSize: config.fontSize || "1rem",
              fontFamily: config.fontFamily || "inherit",
            }}
          >
            {config.options?.map((option, index) => (
              <option
                key={index}
                value={option.value}
                className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
              >
                {option.label}
              </option>
            ))}
          </select>
        );
      case "checkbox":
        return (
          <div className="space-y-2">
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600 border-gray-300 rounded hover:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
              required={config.required}
              onClick={handleInputClick}
              onChange={(e) => handleChange(e)}
            />
            {config.description && (
              <span className="ml-2 text-gray-900 dark:text-gray-100">{config.description}</span>
            )}
          </div>
        );
      case "radio":
        return (
          <div className="space-y-2">
            {config.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <div className="relative">
                  <input
                    type="radio"
                    name={preview ? `preview_${id}` : id}
                    value={option.value}
                    checked={String(selectedValue) === String(option.value)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedValue(value);
                      onChange?.(value);
                    }}
                    className="absolute w-0 h-0 opacity-0"
                  />
                  <div
                    className={`w-5 h-5 border-2 rounded-full transition-colors ${
                      String(selectedValue) === String(option.value)
                        ? "border-blue-600 bg-blue-600"
                        : "border-gray-300 bg-surface-default dark:bg-surface-default"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 mx-auto mt-1 rounded-full bg-surface-default dark:bg-surface-default ${
                        String(selectedValue) === String(option.value)
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    />
                  </div>
                </div>
                <span className="text-gray-900 dark:text-gray-100">{option.label}</span>
              </label>
            ))}
          </div>
        );
      case "date":
        return (
          <input
            type="date"
            className={baseInputStyles}
            required={config.required}
            onClick={handleInputClick}
            onChange={(e) => handleChange(e)}
            style={{
              fontSize: config.fontSize || "1rem",
              fontFamily: config.fontFamily || "inherit",
            }}
          />
        );
      case "file":
        return (
          <div className="space-y-2">
            <input
              type="file"
              className={baseInputStyles}
              required={config.required}
              accept={config.accept}
              onClick={handleInputClick}
              onChange={(e) => handleChange(e)}
              style={{
                fontSize: config.fontSize || "1rem",
                fontFamily: config.fontFamily || "inherit",
              }}
            />
            {config.showPreview && (
              <div className="mt-2 p-2 border rounded-lg bg-gray-50">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Preview will be shown here
                </p>
              </div>
            )}
          </div>
        );
      case "rating":
        return (
          <div className="flex space-x-2">
            {Array.from({ length: config.maxRating || 5 }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={`p-2 rounded-lg transition-colors ${
                  index < (config.value || 0)
                    ? "bg-yellow-400 hover:bg-yellow-500"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
                onClick={handleInputClick}
                onChange={(e) => handleChange(e)}
              >
                ★
              </button>
            ))}
          </div>
        );
      case "page-separator":
        return (
          <div className="relative py-8">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-surface-default dark:bg-surface-default text-lg font-medium text-gray-900 dark:text-gray-100">
                {label || "Page Break"}
              </span>
            </div>
            {config.description && (
              <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-500">
                {config.description}
              </p>
            )}
          </div>
        );
      case "richtext":
        return (
          <div
            className="w-full min-h-[200px] p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
            contentEditable={!preview}
            onClick={handleInputClick}
            onChange={(e) => handleChange(e)}
            style={{
              fontSize: config.fontSize || "1rem",
              fontFamily: config.fontFamily || "inherit",
            }}
          />
        );
      case "signature":
        return (
          <div className="w-full h-[150px] border rounded-lg bg-surface-default dark:bg-surface-default">
            <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-500">
              Signature Pad (Click to sign)
            </div>
          </div>
        );
      case "progress":
        const progress = config.progress || 0;
        return (
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mb-4">
      {type !== "page-separator" && (
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          {label}
          {config.required && (
            <span className="text-red-500 text-xs ml-1">*</span>
          )}
        </label>
      )}
      {renderElement()}
    </div>
  );
};

const DraggableElement = ({
  id,
  type,
  label,
  category,
  icon,
  onAdd,
  showNotification,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedField, setSelectedField] = useState("");

  const isCustomElement =
    type === "custom" || type === "page-separator" || type === "progress";

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        id,
        type,
        label,
        dbField: selectedField,
        config: selectedField ? getDbFieldConfig(selectedField) : {},
      }),
    );

    const ghost = document.createElement("div");
    ghost.className =
      "bg-surface-default dark:bg-surface-default p-3 rounded-lg shadow-lg dark:shadow-none border-2 border-blue-500 opacity-90";
    ghost.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="text-blue-500">${icon}</div>
        <div>
          <p class="font-medium text-gray-900 dark:text-gray-100">${selectedField ? formatDbFieldToLabel(selectedField) : label}</p>
          <p class="text-sm text-gray-900 dark:text-gray-100">${category}</p>
        </div>
      </div>
    `;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleFieldSelect = (e) => {
    setSelectedField(e.target.value);
  };

  const handleAdd = () => {
    if (!selectedField && !isCustomElement) {
      showNotification("Please select a database field", "warning");
      return;
    }

    const config = selectedField ? getDbFieldConfig(selectedField) : {};
    onAdd({
      id: `${type}-${Date.now()}`,
      type,
      label: selectedField ? formatDbFieldToLabel(selectedField) : label,
      dbField: selectedField,
      config: {
        ...config,
        required: config?.required || false,
        editable: config?.editable !== false,
        helpText: config?.helpText || "",
        options: config?.options || [],
      },
    });
    setSelectedField("");
  };

  return (
    <div
      draggable={selectedField || isCustomElement}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`${styles.cards.base} ${styles.cards.hover} p-3 transition-all duration-200
        ${isDragging ? "opacity-50 scale-95" : "opacity-100 scale-100"}
        ${!selectedField && !isCustomElement ? "cursor-default" : "cursor-move"}
      `}
      data-tooltip-id={`element-tooltip-${id}`}
    >
      <div className="flex items-center space-x-3">
        <div
          className={`text-gray-400 group-hover:text-blue-500 transition-colors ${
            isDragging ? "text-blue-500" : ""
          }`}
        >
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-gray-900 dark:text-gray-100 group-hover:text-blue-600 transition-colors">
            {selectedField ? formatDbFieldToLabel(selectedField) : label}
          </p>
          <p className="text-gray-900 dark:text-gray-100 text-sm mt-0.5">{category}</p>

          {!isCustomElement && (
            <select
              value={selectedField}
              onChange={handleFieldSelect}
              className="mt-2 w-full text-sm p-1 border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
            >
              <option value="" className="text-gray-900 dark:text-gray-100">
                Select database field
              </option>
              {getAllowedFields()
                .filter((field) => {
                  const config = getDbFieldConfig(field);
                  return config?.type === type;
                })
                .map((field) => (
                  <option key={field} value={field} className="text-gray-900 dark:text-gray-100">
                    {formatDbFieldToLabel(field)}
                  </option>
                ))}
            </select>
          )}
        </div>
      </div>

      <button
        onClick={handleAdd}
        className={`mt-2 w-full px-2 py-1 text-sm rounded transition-colors ${
          selectedField || isCustomElement
            ? "bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800"
            : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
        }`}
      >
        Add to Form
      </button>

      <Tooltip id={`element-tooltip-${id}`}>
        {isCustomElement
          ? "Add to your form"
          : selectedField
            ? `Add ${formatDbFieldToLabel(selectedField)} field to your form`
            : "Select a database field to add"}
      </Tooltip>
    </div>
  );
};

const PropertiesPanel = ({
  element,
  onConfigChange,
  onDelete,
  formElements,
  formName,
  setFormName,
  firstSectionName,
  setFirstSectionName,
}) => {
  const [isElementPropertiesExpanded, setIsElementPropertiesExpanded] =
    useState(!!element);

  const fontFamilies = [
    { value: "inherit", label: "Default" },
    { value: "Arial, sans-serif", label: "Arial" },
    { value: "Helvetica, sans-serif", label: "Helvetica" },
    { value: "Times New Roman, serif", label: "Times New Roman" },
    { value: "Georgia, serif", label: "Georgia" },
  ];

  const renderColorPicker = (field, label) => (
    <div className="space-y-2">
      <label className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            value={element.config[field] || "#000000"}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-8 h-8 rounded cursor-pointer"
          />
          <input
            type="text"
            value={element.config[field] || "#000000"}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-20 text-sm p-1 border rounded"
          />
        </div>
      </label>
    </div>
  );

  const renderSlider = (field, label, min, max, step = 1) => (
    <div className="space-y-2">
      <label className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <input
          type="number"
          value={element.config[field] || min}
          onChange={(e) => handleChange(field, Number(e.target.value))}
          className="w-16 text-sm p-1 border rounded"
          min={min}
          max={max}
          step={step}
        />
      </label>
      <input
        type="range"
        value={element.config[field] || min}
        onChange={(e) => handleChange(field, Number(e.target.value))}
        className="w-full"
        min={min}
        max={max}
        step={step}
      />
    </div>
  );

  useEffect(() => {
    setIsElementPropertiesExpanded(!!element);
  }, [element]);

  const handleChange = (field, value) => {
    if (field === "label") {
      // Always allow label changes for non-database fields
      if (!element.dbField) {
        onConfigChange({ ...element, label: value });
        return;
      }

      // For database fields, check if composite
      const dbConfig = getDbFieldConfig(element.dbField);
      if (dbConfig?.isComposite || dbConfig?.editable) {
        onConfigChange({ ...element, label: value });
        return;
      }

      // For non-editable database fields, don't allow changes
      return;
    }

    // Handle all other field changes
    const newConfig = { ...element.config };
    const dbConfig = element.dbField ? getDbFieldConfig(element.dbField) : null;

    // For database fields, check if editable
    if (element.dbField && !dbConfig?.isComposite) {
      if (!dbConfig?.editable) {
        return;
      }
    }

    // Handle the change
    newConfig[field] = value;
    onConfigChange({ ...element, config: newConfig });
  };

  const handleConditionChange = (condition) => {
    const updatedElement = { ...element };
    updatedElement.config = {
      ...element.config,
      conditional: true,
      conditions: {
        ...element.config.conditions,
        ...condition,
        operator: condition.operator || "equals",
      },
    };
    onConfigChange(updatedElement);
  };

  const availableFields = formElements
    .filter((el) => el.id !== element.id)
    .map((el) => ({
      id: el.id,
      label: el.label,
      type: el.type,
    }));

  const isFieldEditable = (field) => {
    if (!element.dbField) return true;

    const dbConfig = getDbFieldConfig(element.dbField);

    // For composite fields (like Full Name), allow editing all fields except required status
    if (dbConfig?.isComposite) {
      return field !== "required";
    }

    // For regular database fields
    if (field === "required") return false;
    if (!dbConfig?.editable) return false;

    return true;
  };

  const getFieldValue = (field) => {
    if (!element.dbField) {
      return field === "label" ? element.label : element.config?.[field];
    }

    const dbConfig = getDbFieldConfig(element.dbField);

    // For composite fields (like Full Name), use the element's values
    if (dbConfig?.isComposite) {
      if (field === "required") return dbConfig.required || false;
      if (field === "label") return element.label;
      return element.config?.[field];
    }

    // For regular database fields
    if (field === "required") return dbConfig?.required || false;
    if (field === "label") return dbConfig?.label || element.label;
    if (field === "helpText")
      return dbConfig?.helpText || element.config?.helpText;

    return element.config?.[field];
  };

  const renderPreview = () => (
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview</p>
      <div className="bg-surface-default dark:bg-surface-default p-4 rounded border">
        <FormElement {...element} preview={true} formElements={formElements} />
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Form Settings Section - Always Visible */}
      <div className="p-4 border-b">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Form Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Form Name
            </label>
            <input
              type="text"
              value={formName || ""}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter form name"
              className={baseInputStyles}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              First Section Name
            </label>
            <input
              type="text"
              value={firstSectionName || ""}
              onChange={(e) => setFirstSectionName(e.target.value)}
              placeholder="Name for the first section"
              className={baseInputStyles}
            />
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              This will be the name of the first section of your form
            </p>
          </div>
        </div>
      </div>
      {/* Element Properties Section - Expandable */}
      {element && (
        <div className="flex-1 overflow-auto">
          <div
            className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-between"
            onClick={() =>
              setIsElementPropertiesExpanded(!isElementPropertiesExpanded)
            }
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Element Properties
            </h3>
            <button className="p-1 hover:bg-gray-200 rounded-full transition-colors">
              <svg
                className={`w-6 h-6 transform transition-transform ${isElementPropertiesExpanded ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          <div
            className={`transition-all duration-300 ${isElementPropertiesExpanded ? "opacity-100" : "opacity-0 h-0 overflow-hidden"}`}
          >
            <div className="p-4 space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Selected Element:
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {element.label || element.type}
                  </span>
                </div>
                <button
                  onClick={() => onDelete(element.id)}
                  className="px-3 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1"
                  data-tooltip-id="delete-field-tooltip"
                >
                  <span>Delete Field</span>
                </button>
              </div>

              {/* Database Field Info */}
              {element.dbField && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Database Field
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        {getDbFieldConfig(element.dbField)?.isComposite ? (
                          <>
                            <p>This field maps to multiple database fields:</p>
                            <ul className="list-disc ml-4 mt-1">
                              <li>First Name (required)</li>
                              <li>Middle Name (optional)</li>
                              <li>Last Name (required)</li>
                            </ul>
                            <p className="mt-1">
                              You can customize the question text while
                              maintaining these mappings.
                            </p>
                          </>
                        ) : (
                          <>
                            <p>
                              This field is mapped to the database field:{" "}
                              <code>{element.dbField}</code>
                            </p>
                            <p className="mt-1">
                              Some settings are controlled by the database
                              schema and cannot be modified.
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Basic Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b pb-2">
                  Basic Settings
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Label
                    <span className="text-xs text-gray-500 dark:text-gray-500 ml-2">
                      (The text shown above the field)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={getFieldValue("label")}
                    onChange={(e) => handleChange("label", e.target.value)}
                    className={`w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default transition-colors ${
                      isFieldEditable("label")
                        ? "hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        : "bg-gray-50 cursor-not-allowed"
                    }`}
                    disabled={!isFieldEditable("label")}
                  />
                  {element.dbField && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                      This label is controlled by the database schema
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Help Text
                    <span className="text-xs text-gray-500 dark:text-gray-500 ml-2">
                      (Additional guidance for users)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={getFieldValue("helpText")}
                    onChange={(e) => handleChange("helpText", e.target.value)}
                    className={`w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default transition-colors ${
                      isFieldEditable("helpText")
                        ? "hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        : "bg-gray-50 cursor-not-allowed"
                    }`}
                    disabled={!isFieldEditable("helpText")}
                    placeholder="Optional help text"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      checked={getFieldValue("required")}
                      onChange={(e) =>
                        handleChange("required", e.target.checked)
                      }
                      disabled={!isFieldEditable("required")}
                      className={`toggle-checkbox absolute block w-6 h-6 rounded-full bg-surface-default dark:bg-surface-default border-4 appearance-none cursor-pointer ${
                        !isFieldEditable("required") ? "cursor-not-allowed" : ""
                      }`}
                    />
                    <label
                      className={`toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                        !isFieldEditable("required") ? "cursor-not-allowed" : ""
                      }`}
                    ></label>
                  </div>
                  <span className="text-sm text-gray-900 dark:text-gray-100">Required field</span>
                  {element.dbField && (
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      (Controlled by database schema)
                    </span>
                  )}
                </div>
              </div>

              {/* Styling */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b pb-2">
                  Styling
                </h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Font Family
                    </label>
                    <select
                      value={element.config.fontFamily || "inherit"}
                      onChange={(e) =>
                        handleChange("fontFamily", e.target.value)
                      }
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                    >
                      {fontFamilies.map((font) => (
                        <option
                          key={font.value}
                          value={font.value}
                          className="text-gray-900 dark:text-gray-100"
                        >
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {renderSlider("fontSize", "Font Size", 12, 24)}
                  {renderColorPicker("textColor", "Text Color")}
                  {renderColorPicker("backgroundColor", "Background Color")}
                </div>
              </div>

              {/* Field-specific Settings */}
              {element.type === "text" && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b pb-2">
                    Text Field Settings
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={element.config.placeholder || ""}
                      onChange={(e) =>
                        handleChange("placeholder", e.target.value)
                      }
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                    />
                  </div>
                  {renderSlider("minLength", "Minimum Length", 0, 100)}
                  {renderSlider("maxLength", "Maximum Length", 0, 1000)}
                </div>
              )}

              {(element.type === "dropdown" ||
                element.type === "radio" ||
                element.type === "multiselect") && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b pb-2">
                    Options
                  </h4>
                  <div className="space-y-2">
                    {(element.config.options || []).map((option, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => {
                            const newOptions = [
                              ...(element.config.options || []),
                            ];
                            newOptions[index] = {
                              ...option,
                              label: e.target.value,
                              value: e.target.value
                                .toLowerCase()
                                .replace(/\s+/g, "-"),
                            };
                            handleChange("options", newOptions);
                          }}
                          className="flex-1 p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                          placeholder="Option label"
                        />
                        <button
                          onClick={() => {
                            const newOptions = [
                              ...(element.config.options || []),
                            ];
                            newOptions.splice(index, 1);
                            handleChange("options", newOptions);
                          }}
                          className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newOptions = [
                          ...(element.config.options || []),
                          { label: "", value: "" },
                        ];
                        handleChange("options", newOptions);
                      }}
                      className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 transition-colors"
                    >
                      + Add Option
                    </button>
                  </div>
                </div>
              )}

              {/* Conditional Display Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 border-b pb-2">
                  Display Logic
                </h4>
                <div className="flex items-center space-x-2">
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      checked={element.config.conditional || false}
                      onChange={(e) =>
                        handleChange("conditional", e.target.checked)
                      }
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-surface-default dark:bg-surface-default border-4 appearance-none cursor-pointer"
                    />
                    <label className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    Enable conditional display
                  </span>
                </div>

                {element.config.conditional && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Show this field when:
                      </label>
                      <select
                        value={element.config.conditions?.fieldId || ""}
                        onChange={(e) =>
                          handleConditionChange({ fieldId: e.target.value })
                        }
                        className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                      >
                        <option value="">Select a field</option>
                        {availableFields.map((field) => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {element.config.conditions?.fieldId && (
                      <>
                        <div>
                          <select
                            value={
                              element.config.conditions?.operator || "equals"
                            }
                            onChange={(e) =>
                              handleConditionChange({
                                operator: e.target.value,
                              })
                            }
                            className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                          >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Does not equal</option>
                            <option value="contains">Contains</option>
                            <option value="not_contains">
                              Does not contain
                            </option>
                            <option value="is_empty">Is empty</option>
                            <option value="is_not_empty">Is not empty</option>
                            {["checkbox", "radio"].includes(
                              formElements.find(
                                (el) =>
                                  el.id === element.config.conditions?.fieldId,
                              )?.type,
                            ) && <option value="is_checked">Is checked</option>}
                          </select>
                        </div>

                        {!["is_empty", "is_not_empty", "is_checked"].includes(
                          element.config.conditions?.operator,
                        ) && (
                          <div>
                            <input
                              type="text"
                              value={element.config.conditions?.value || ""}
                              onChange={(e) =>
                                handleConditionChange({ value: e.target.value })
                              }
                              placeholder="Enter value"
                              className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Preview */}
              {renderPreview()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SortableFormElement = ({
  element,
  index,
  isSelected,
  onSelect,
  onConfigChange,
  formElements,
  setFormElements,
  addToHistory,
  setSelectedElement,
  showNotification,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: element.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isSelected ? 1 : "auto",
    pointerEvents: isDragging ? "none" : "auto",
  };

  const [isHovered, setIsHovered] = useState(false);
  const [isClickable, setIsClickable] = useState(true);

  useEffect(() => {
    if (isDragging) {
      setIsClickable(false);
    } else {
      const timer = setTimeout(() => {
        setIsClickable(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isDragging]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isClickable || isDragging) return;
    setTimeout(() => {
      onSelect(element);
    }, 50);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-element-id={element.id}
      className={`
        form-element relative group transition-all duration-300
        ${isSelected ? `${styles.cards.selected} scale-[1.02]` : "hover:scale-[1.01]"}
        ${styles.cards.base} ${styles.shadows.sm}
        ${isDragging ? "rotate-1" : ""}
      `}
    >
      {/* Drag Handle */}
      <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-move opacity-0 group-hover:opacity-100 transition-opacity">
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 8h16M4 16h16"
          />
        </svg>
      </div>

      {/* Element Content */}
      <div className="pl-8">
        <FormElement {...element} onConfigChange={onConfigChange} />
      </div>

      {/* Quick Actions */}
      <div
        className={`
          absolute right-2 top-2 flex items-center space-x-1
          transition-all duration-200
          ${isHovered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}
        `}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            const updatedElement = { ...element };
            updatedElement.config = {
              ...element.config,
              required: !element.config.required,
            };
            onConfigChange(updatedElement);
            showNotification(
              `Field ${updatedElement.config.required ? "marked as required" : "marked as optional"}`,
              "info",
            );
          }}
          className={`${styles.buttons.icon} p-1`}
          data-tooltip-id={`required-tooltip-${element.id}`}
        >
          {element.config.required ? (
            <svg
              className="w-4 h-4 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          ) : (
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
              />
            </svg>
          )}
        </button>
        <Tooltip id={`required-tooltip-${element.id}`}>
          {element.config.required ? "Make optional" : "Make required"}
        </Tooltip>

        <button
          onClick={(e) => {
            e.stopPropagation();
            // Duplicate element
            const newElement = {
              ...element,
              id: `${element.type}-${Date.now()}`,
            };
            const newElements = [...formElements];
            newElements.splice(index + 1, 0, newElement);
            setFormElements(newElements);
            addToHistory(newElements);
            showNotification("Element duplicated", "success");
          }}
          className={`${styles.buttons.icon} p-1`}
          data-tooltip-id={`duplicate-tooltip-${element.id}`}
        >
          <svg
            className="w-4 h-4 text-gray-400 hover:text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </button>
        <Tooltip id={`duplicate-tooltip-${element.id}`}>
          Duplicate field
        </Tooltip>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect(element);
          }}
          className={`${styles.buttons.icon} p-1`}
          data-tooltip-id={`edit-tooltip-${element.id}`}
        >
          <svg
            className="w-4 h-4 text-gray-400 hover:text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <Tooltip id={`edit-tooltip-${element.id}`}>Edit field</Tooltip>

        <button
          onClick={(e) => {
            e.stopPropagation();
            const confirmed = window.confirm(
              "Are you sure you want to delete this field?",
            );
            if (confirmed) {
              const newElements = formElements.filter(
                (el) => el.id !== element.id,
              );
              setFormElements(newElements);
              addToHistory(newElements);
              if (isSelected) {
                setSelectedElement(null);
              }
              showNotification("Field deleted", "info");
            }
          }}
          className={`${styles.buttons.icon} p-1`}
          data-tooltip-id={`delete-tooltip-${element.id}`}
        >
          <svg
            className="w-4 h-4 text-gray-400 hover:text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
        <Tooltip id={`delete-tooltip-${element.id}`}>Delete field</Tooltip>
      </div>

      {/* Drop Indicator */}
      {isDragging && (
        <div className="absolute inset-0 border-2 border-dashed border-blue-500 rounded-lg pointer-events-none" />
      )}
    </div>
  );
};

const ShortcutsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const shortcuts = [
    { keys: ["Ctrl", "Z"], description: "Undo" },
    { keys: ["Ctrl", "Y"], description: "Redo" },
    { keys: ["Ctrl", "S"], description: "Save template" },
    { keys: ["Ctrl", "P"], description: "Toggle preview" },
    { keys: ["Ctrl", "T"], description: "Open templates" },
    { keys: ["Ctrl", "G"], description: "Generate with AI" },
    { keys: ["Ctrl", "K"], description: "Show keyboard shortcuts" },
    { keys: ["Delete"], description: "Delete selected field" },
    { keys: ["Escape"], description: "Close modal / Deselect field" },
    { keys: ["Ctrl", "↑"], description: "Move field up" },
    { keys: ["Ctrl", "↓"], description: "Move field down" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="xl">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Keyboard Shortcuts
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            ×
          </button>
        </div>
        <div className="space-y-4">
          {shortcuts.map((shortcut, index) => (
            <div
              key={index}
              className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-neutral-700 last:border-0"
            >
              <span className="text-gray-900 dark:text-gray-100">
                {shortcut.description}
              </span>
              <div className="flex gap-2">
                {shortcut.keys.map((key, keyIndex) => (
                  <kbd
                    key={keyIndex}
                    className="px-2 py-1 text-sm font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded"
                  >
                    {key}
                  </kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

const LogoSettingsModal = ({ isOpen, onClose, initialLogo, onSave }) => {
  const [logoSettings, setLogoSettings] = useState(initialLogo);
  const [selectedLogo, setSelectedLogo] = useState(initialLogo.src);

  if (!isOpen) return null;

  const availableLogos = [
    { src: "/images/logos/q-logo-color.png", label: "Long Logo" },
    { src: "/images/logos/q-logo-color.png", label: "Default Logo" },
    { src: "/images/logos/logo_short.png", label: "Short Logo" },
    { src: "/images/logos/RS_logo_icon_outlined..png", label: "Icon Logo" },
  ];

  const handleSave = () => {
    onSave(logoSettings);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Logo Settings
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Select Logo
            </label>
            <div className="grid grid-cols-2 gap-4">
              {availableLogos.map((logo) => (
                <div
                  key={logo.src}
                  onClick={() => {
                    setLogoSettings((prev) => ({ ...prev, src: logo.src }));
                    setSelectedLogo(logo.src);
                  }}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedLogo === logo.src
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400"
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-full h-24 flex items-center justify-center mb-2 bg-white dark:bg-gray-800 rounded-md p-2">
                      <img
                        src={logo.src}
                        alt={logo.label}
                        className="w-auto h-auto max-h-full object-contain"
                      />
                    </div>
                    <p className="text-sm text-center text-gray-900 dark:text-gray-100">
                      {logo.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Logo Width (px)
            </label>
            <input
              type="number"
              value={logoSettings.width}
              onChange={(e) =>
                setLogoSettings((prev) => ({
                  ...prev,
                  width: parseInt(e.target.value),
                }))
              }
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              min="50"
              max="500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Alignment
            </label>
            <select
              value={logoSettings.alignment}
              onChange={(e) =>
                setLogoSettings((prev) => ({
                  ...prev,
                  alignment: e.target.value,
                }))
              }
              className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>

          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Preview</h4>
            <div className="flex items-center justify-center p-4 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <img
                src={selectedLogo}
                alt="Selected Logo"
                style={{
                  width: `${logoSettings.width}px`,
                  maxWidth: "100%",
                  height: "auto",
                }}
                className="object-contain"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "danger":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        );
      case "warning":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} maxWidth="md">
      <div className="p-6">
        <div className="text-center">
          {getIcon()}
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">{message}</p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${
                type === "danger"
                  ? "bg-red-600 hover:bg-red-700"
                  : type === "warning"
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Database field mapping utilities
const DB_FIELD_MAPPINGS = {
  name_id: {
    label: "Name ID",
    type: "text",
    editable: false,
    required: true,
    helpText: "System-generated ID",
  },
  prefix: {
    label: "Prefix",
    type: "dropdown",
    editable: true,
    options: [
      { label: "Mr.", value: "Mr." },
      { label: "Mrs.", value: "Mrs." },
      { label: "Ms.", value: "Ms." },
      { label: "Dr.", value: "Dr." },
    ],
    helpText: "Title or honorific",
  },
  first_name: {
    label: "First Name",
    type: "text",
    editable: true,
    required: true,
    helpText: "Legal first name",
  },
  middle_name: {
    label: "Middle Name",
    type: "text",
    editable: true,
    helpText: "Middle name or initial",
  },
  last_name: {
    label: "Last Name",
    type: "text",
    editable: true,
    required: true,
    helpText: "Legal last name",
  },
  suffix: {
    label: "Suffix",
    type: "dropdown",
    editable: true,
    options: [
      { label: "Jr.", value: "Jr." },
      { label: "Sr.", value: "Sr." },
      { label: "II", value: "II" },
      { label: "III", value: "III" },
      { label: "IV", value: "IV" },
    ],
    helpText: "Name suffix if applicable",
  },
  preferred_name: {
    label: "Preferred Name",
    type: "text",
    editable: true,
    helpText: "Name you prefer to be called",
  },
  created_at: {
    label: "Created At",
    type: "text",
    editable: false,
    helpText: "Record creation timestamp",
  },
  updated_at: {
    label: "Updated At",
    type: "text",
    editable: false,
    helpText: "Last update timestamp",
  },
  // Add composite field for full name
  full_name: {
    label: "Full Name",
    type: "text",
    editable: true,
    isComposite: true,
    required: true,
    helpText: "Enter your full legal name",
    fields: [
      {
        dbField: "first_name",
        label: "First Name",
        placeholder: "First",
        required: true,
      },
      {
        dbField: "middle_name",
        label: "Middle Name",
        placeholder: "Middle",
        required: false,
      },
      {
        dbField: "last_name",
        label: "Last Name",
        placeholder: "Last",
        required: true,
      },
    ],
  },
};

const getDbFieldConfig = (fieldName) => {
  return DB_FIELD_MAPPINGS[fieldName] || null;
};

const isSystemField = (fieldName) => {
  return ["name_id", "created_at", "updated_at"].includes(fieldName);
};

const getAllowedFields = () => {
  return Object.keys(DB_FIELD_MAPPINGS).filter(
    (field) => !isSystemField(field),
  );
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const validateFieldName = (fieldName) => {
  return !!DB_FIELD_MAPPINGS[fieldName];
};

const formatDbFieldToLabel = (fieldName) => {
  const config = getDbFieldConfig(fieldName);
  return config
    ? config.label
    : fieldName
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Add this helper function to get section names
const getSectionNames = (formElements, formName, firstSectionName) => {
  const sectionNames = [];

  // Add first section name if provided, otherwise leave empty
  sectionNames.push(firstSectionName || "");

  // Get names from page separators
  formElements.forEach((element) => {
    if (element.type === "page-separator" && element.config) {
      sectionNames.push(element.config.nextSectionName || "");
    }
  });

  return sectionNames;
};

const Campaigns = () => {
  const [formElements, setFormElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showPreview, setShowPreview] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [formLogo, setFormLogo] = useState({
    src: "/images/logos/q-logo-color.png", // Update this path
    width: 200,
    alignment: "center",
  });
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({});
  const [formName, setFormName] = useState("");
  const [firstSectionName, setFirstSectionName] = useState(""); // Add state for first section name
  const notificationIdRef = useRef(0);

  // Add new state for campaign type
  const [showCampaignTypeModal, setShowCampaignTypeModal] = useState(false);
  const [campaignType, setCampaignType] = useState(null);
  const [audienceType, setAudienceType] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [recipientList, setRecipientList] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [embedSettings, setEmbedSettings] = useState({
    buttonText: "Take Survey",
    buttonColor: "#3B82F6",
    buttonTextColor: "#FFFFFF",
    buttonFontSize: "16px",
    buttonPadding: "12px 24px",
    buttonBorderRadius: "8px",
    buttonHoverColor: "#2563EB",
    inheritStyles: false,
    utmSource: "",
    utmMedium: "",
    utmCampaign: "",
  });

  // UI state
  const [dragOver, setDragOver] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [expandedCategory, setExpandedCategory] = useState("Basic Inputs");

  // Modal state
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNameDraftModal, setShowNameDraftModal] = useState(false);
  const [showLogoSettings, setShowLogoSettings] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showConfirmRefresh, setShowConfirmRefresh] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);

  // Templates and drafts state
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [isDraft, setIsDraft] = useState(false);
  const [draftNameInput, setDraftNameInput] = useState("");
  const [deletingDraftId, setDeletingDraftId] = useState(null);

  // AI and file upload state
  const [aiPrompt, setAiPrompt] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [embedCode, setEmbedCode] = useState("");
  const [showAIModal, setShowAIModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Add new state for template name modal
  const [showTemplateNameModal, setShowTemplateNameModal] = useState(false);

  // Add new state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Validate current section when form elements change
  useEffect(() => {
    if (showPreview) {
      const sections = formElements.reduce(
        (acc, element) => {
          if (element.type === "page-separator") {
            if (acc.currentSection.length > 0) {
              acc.sections.push({
                elements: acc.currentSection,
                title: element.config.title || "Form Section",
                description: element.config.description,
              });
            }
            acc.currentSection = [];
          } else {
            acc.currentSection.push(element);
          }
          return acc;
        },
        { sections: [], currentSection: [] },
      );

      // Add the last section if it has elements
      if (sections.currentSection.length > 0) {
        sections.sections.push({
          elements: sections.currentSection,
          title: "Final Section",
          description: "",
        });
      }

      // If no sections but we have elements, create a single section
      if (sections.sections.length === 0 && formElements.length > 0) {
        sections.sections.push({
          elements: formElements,
          title: formName || "Form",
          description: "",
        });
      }

      // Ensure currentSection is valid
      if (currentSection >= sections.sections.length) {
        setCurrentSection(0);
      }
    }
  }, [formElements, showPreview, currentSection, formName]);

  // Define showNotification before any usage
  const showNotification = useCallback(
    (message, type = "info", duration = 5000) => {
      const id = notificationIdRef.current++;
      const notification = {
        id,
        message,
        type,
        title: type.charAt(0).toUpperCase() + type.slice(1),
        icon:
          type === "success" ? (
            <svg
              className="w-5 h-5 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : type === "error" ? (
            <svg
              className="w-5 h-5 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : type === "warning" ? (
            <svg
              className="w-5 h-5 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
      };

      setNotifications((prev) => [...prev, notification]);

      if (duration > 0) {
        setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, duration);
      }

      return id;
    },
    [],
  );

  const breadcrumbItems = [
    { label: "Marketing & Sales", link: "/marketing-sales" },
    { label: "Campaigns", link: "/marketing-sales/campaigns" },
  ];

  const availableElements = [
    {
      category: "Basic Inputs",
      items: [
        {
          id: "text",
          type: "text",
          label: "Text Field",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          ),
        },
        {
          id: "dropdown",
          type: "dropdown",
          label: "Dropdown",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          ),
        },
        {
          id: "multiselect",
          type: "multiselect",
          label: "Multi-Select",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          ),
        },
      ],
    },
    {
      category: "Selection Controls",
      items: [
        {
          id: "checkbox",
          type: "checkbox",
          label: "Checkbox",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ),
        },
        {
          id: "radio",
          type: "radio",
          label: "Radio Group",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <circle cx="12" cy="12" r="10" strokeWidth={2} />
              <circle cx="12" cy="12" r="3" strokeWidth={2} />
            </svg>
          ),
        },
      ],
    },
    {
      category: "Advanced Inputs",
      items: [
        {
          id: "date",
          type: "date",
          label: "Date Picker",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          ),
        },
        {
          id: "file",
          type: "file",
          label: "File Upload",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          ),
        },
        {
          id: "richtext",
          type: "richtext",
          label: "Rich Text Editor",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          ),
        },
      ],
    },
    {
      category: "Special Elements",
      items: [
        {
          id: "page-separator",
          type: "page-separator",
          label: "Page Break",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 12h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12a2 2 0 100 4h14a2 2 0 100-4"
              />
            </svg>
          ),
        },
        {
          id: "rating",
          type: "rating",
          label: "Rating Scale",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          ),
        },
        {
          id: "signature",
          type: "signature",
          label: "Signature",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          ),
        },
        {
          id: "progress",
          type: "progress",
          label: "Progress Bar",
          icon: (
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          ),
        },
      ],
    },
  ];

  // Enhanced addToHistory function
  const addToHistory = useCallback(
    (elements) => {
      if (
        historyIndex >= 0 &&
        JSON.stringify(history[historyIndex]) === JSON.stringify(elements)
      )
        return;
      const newHistory = history.slice(0, historyIndex + 1);
      setHistory([...newHistory, elements]);
      setHistoryIndex(newHistory.length);
    },
    [history, historyIndex],
  );

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setFormElements(history[historyIndex - 1]);
    }
  }, [historyIndex, history]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setFormElements(history[historyIndex + 1]);
    }
  }, [historyIndex, history]);

  // Delete field
  const handleDeleteField = useCallback(
    (fieldId) => {
      const updatedElements = formElements.filter((el) => el.id !== fieldId);
      setFormElements(updatedElements);
      setSelectedElement(null);
      addToHistory(updatedElements);
    },
    [formElements, addToHistory],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDraggingOver(true);

    // Add visual cues for drop target
    const dropZone = e.currentTarget;
    dropZone.style.transform = "scale(1.01)";
    dropZone.style.boxShadow = "0 0 0 2px rgba(59, 130, 246, 0.5)";
  }, []);

  const handleDragLeave = useCallback((e) => {
    setIsDraggingOver(false);

    // Reset visual cues
    const dropZone = e.currentTarget;
    dropZone.style.transform = "";
    dropZone.style.boxShadow = "";
  }, []);

  // Handle new element drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDraggingOver(false);

      // Reset visual cues
      const dropZone = e.currentTarget;
      dropZone.style.transform = "";
      dropZone.style.boxShadow = "";

      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      const dropPosition = {
        x: e.clientX,
        y: e.clientY,
      };

      // Find the closest element to drop position
      const elements = dropZone.querySelectorAll(".form-element");
      let insertIndex = formElements.length;

      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        if (dropPosition.y < rect.top + rect.height / 2) {
          insertIndex = Math.min(insertIndex, index);
        }
      });

      const newElement = {
        ...data,
        id: `${data.type}-${Date.now()}`,
        config: {},
      };

      const newElements = [...formElements];
      newElements.splice(insertIndex, 0, newElement);
      setFormElements(newElements);
      setSelectedElement(newElement);
      addToHistory(newElements);

      // Show success notification
      showNotification(`Added ${data.label} element`, "success");

      // Animate the new element
      setTimeout(() => {
        const newElementNode = dropZone.querySelector(
          `[data-element-id="${newElement.id}"]`,
        );
        if (newElementNode) {
          newElementNode.style.animation = "scale-in 0.3s ease-out forwards";
        }
      }, 0);
    },
    [formElements, addToHistory, showNotification],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (!over) return;

      if (active.id !== over.id) {
        setFormElements((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const newElements = arrayMove(items, oldIndex, newIndex);
          addToHistory(newElements);

          // Auto-select the dropped element after a short delay
          setTimeout(() => {
            const droppedElement = newElements[newIndex];
            setSelectedElement(droppedElement);
          }, 50);

          return newElements;
        });
      } else {
        // If element was dragged but dropped in same position, still ensure it's selected
        const element = formElements.find((item) => item.id === active.id);
        if (element) {
          setTimeout(() => {
            setSelectedElement(element);
          }, 50);
        }
      }
    },
    [addToHistory, formElements],
  );

  const handleElementSelect = useCallback((element) => {
    setSelectedElement(element);
  }, []);

  const handleConfigChange = (updatedElement) => {
    const elementIndex = formElements.findIndex(
      (el) => el.id === updatedElement.id,
    );
    if (elementIndex === -1) {
      // This is a new element being added
      const newElements = [...formElements, updatedElement];

      // If this is the second element and there's no page separator yet, add one
      if (
        newElements.length === 2 &&
        !newElements.some((el) => el.type === "page-separator")
      ) {
        // Calculate the next page number
        const existingPageBreaks = formElements.filter(
          (el) => el.type === "page-separator",
        ).length;
        const nextPageNumber = existingPageBreaks + 2; // +2 because we start with Page 1

        const pageBreak = {
          id: `page-separator-${Date.now()}`,
          type: "page-separator",
          label: `Page ${nextPageNumber}`,
          config: {
            description: "",
          },
        };
        newElements.splice(1, 0, pageBreak); // Insert page break after first element
      }

      setFormElements(newElements);
      setSelectedElement(updatedElement);
      addToHistory(newElements);
    } else {
      const newElements = [...formElements];
      newElements[elementIndex] = updatedElement;
      setFormElements(newElements);
      setSelectedElement(updatedElement);
      addToHistory(newElements);
    }
  };

  const handleDeleteDraft = (draftId, e) => {
    e.stopPropagation(); // Prevent template from being loaded when deleting
    setDeletingDraftId(draftId);
    setShowDeleteModal(true);
  };

  const confirmDeleteDraft = () => {
    setDrafts((prev) => prev.filter((draft) => draft.id !== deletingDraftId));
    setNotification({
      show: true,
      message: "Draft deleted successfully!",
      type: "success",
    });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
    setShowDeleteModal(false);
    setDeletingDraftId(null);
  };

  // Update handleSaveTemplate function
  const handleSaveTemplate = (asDraft = false) => {
    if (formElements.length === 0) {
      showNotification(
        "Cannot save an empty form. Please add some elements first.",
        "error",
      );
      return;
    }

    if (!formName.trim()) {
      setShowTemplateNameModal(true);
      return;
    }

    if (asDraft) {
      setDraftNameInput(formName);
      setShowNameDraftModal(true);
    } else {
      setShowCampaignTypeModal(true);
    }
  };

  const saveFinalTemplate = (asDraft = false) => {
    const template = {
      id: Date.now(),
      name: asDraft ? draftNameInput : formName,
      elements: formElements,
      createdAt: new Date().toISOString(),
      isDraft: asDraft,
      campaignSettings: {
        type: campaignType,
        audience: audienceType,
        emailTemplate,
        tokenExpiry,
        embedSettings,
      },
    };

    if (asDraft) {
      setDrafts((prev) => [...prev, template]);
      showNotification("Draft saved successfully!", "success");
    } else {
      setTemplates((prev) => [...prev, template]);

      // Show different success messages based on campaign type
      if (campaignType === "email") {
        showNotification(
          `Template saved! ${audienceType === "all" ? "All clients" : "Selected clients"} will receive an email with their unique form link.`,
          "success",
        );

        // Generate and show UUID links if it's an email campaign
        const uniqueLink = `${window.location.origin}/form/${template.id}?token=UUID_PLACEHOLDER`;
        setEmbedCode(uniqueLink);
        setShowEmbedModal(true);
      } else if (campaignType === "embed") {
        // Generate and show embed code
        const embedCode = `
<!-- Form Widget -->
<div id="form-${template.id}"></div>
<script src="${window.location.origin}/form-widget.js"></script>
<script>
  window.FormWidget.init({
    containerId: 'form-${template.id}',
    formId: '${template.id}',
    formName: '${formName}',
    buttonSettings: {
      text: '${embedSettings.buttonText}',
      color: '${embedSettings.buttonColor}',
      textColor: '${embedSettings.buttonTextColor}',
      fontSize: '${embedSettings.buttonFontSize}',
      padding: '${embedSettings.buttonPadding}',
      borderRadius: '${embedSettings.buttonBorderRadius}',
      hoverColor: '${embedSettings.buttonHoverColor}',
      inheritStyles: ${embedSettings.inheritStyles}
    },
    tracking: {
      utmSource: '${embedSettings.utmSource}',
      utmMedium: '${embedSettings.utmMedium}',
      utmCampaign: '${embedSettings.utmCampaign}'
    }
  });
</script>`;

        setEmbedCode(embedCode);
        setShowEmbedModal(true);
        showNotification(
          "Template saved! You can now embed this form on your website.",
          "success",
        );
      }
    }

    setShowNameDraftModal(false);
    setShowTemplates(false);
    setShowConfirmSave(false);
    setShowCampaignTypeModal(false);
  };

  const handleLoadTemplate = (template) => {
    setFormElements(template.elements);
    setFormName(template.name);
    setIsDraft(template.isDraft || false);

    // Load campaign settings if they exist
    if (template.campaignSettings) {
      setCampaignType(template.campaignSettings.type);
      setAudienceType(template.campaignSettings.audience);
      setEmailTemplate(template.campaignSettings.emailTemplate);
      setTokenExpiry(template.campaignSettings.tokenExpiry);
      setEmbedSettings(template.campaignSettings.embedSettings);
    }

    setShowTemplates(false);
  };

  const handleAIGenerate = async () => {
    const modal = (
      <Modal isOpen={true} onClose={() => setShowAIModal(false)} maxWidth="xl">
        <div className="p-6 bg-white dark:bg-gray-900">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Generate Form with AI
          </h3>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Describe the form you want to create
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={4}
                className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 placeholder-gray-400 dark:placeholder-gray-500"
                placeholder="Describe the form you want to create..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Upload context documents (optional, max 10 files)
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
                  isDragging 
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                } bg-white dark:bg-gray-800`}
                onDragOver={handleDragOver}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleFileDrop}
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400 dark:text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Drag and drop files here, or{" "}
                    <button
                      type="button"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium"
                      onClick={() => document.getElementById("fileInput").click()}
                    >
                      browse
                    </button>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Supported formats: PDF, DOC, DOCX, TXT, RTF
                  </p>
                </div>
                <input
                  id="fileInput"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.txt,.rtf"
                />
              </div>
              {uploadedFiles.length > 0 && (
                <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  {uploadedFiles.map((file, index) => (
                    <li
                      key={index}
                      className="px-4 py-3 flex justify-between items-center"
                    >
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {file.name} ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm transition-colors"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                onClick={() => setShowAIModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowAIModal(false);
                  // TODO: Implement OpenAI API integration
                  const suggestedElements = [
                    {
                      id: "name",
                      type: "text",
                      label: "Full Name",
                      config: {
                        required: true,
                        placeholder: "Enter your full name",
                      },
                    },
                    {
                      id: "email",
                      type: "text",
                      label: "Email Address",
                      config: {
                        required: true,
                        placeholder: "Enter your email",
                        helpText: "We will use this to contact you",
                      },
                    },
                  ];
                  setFormElements(suggestedElements);
                  setUploadedFiles([]);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      </Modal>
    );

    setShowAIModal(true);
    setModalContent(modal);
  };

  const handleGenerateEmbedCode = () => {
    // Generate a unique ID for the form
    const formId = `form-${Date.now()}`;

    // Create the embed code
    const embedCode = `
<!-- Form Widget -->
<div id="${formId}"></div>
<script src="YOUR_DOMAIN/form-widget.js"></script>
<script>
  window.FormWidget.init({
    containerId: '${formId}',
    formId: '${Date.now()}',
    formName: '${formName}',
    elements: ${JSON.stringify(formElements)}
  });
</script>`;

    setEmbedCode(embedCode);
    setShowEmbedModal(true);
  };

  const handleResetForm = () => {
    if (formElements.length === 0) {
      showNotification("Form is already empty", "info");
      return;
    }
    setShowConfirmReset(true);
  };

  // Enhanced keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in input fields
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;

      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z") {
          e.preventDefault();
          if (e.shiftKey) {
            handleRedo();
          } else {
            handleUndo();
          }
        }
        if (e.key === "y") {
          e.preventDefault();
          handleRedo();
        }
        if (e.key === "s") {
          e.preventDefault();
          handleSaveTemplate();
        }
        if (e.key === "p") {
          e.preventDefault();
          setShowPreview(!showPreview);
        }
        if (e.key === "t") {
          e.preventDefault();
          setShowTemplates(true);
        }
        if (e.key === "g") {
          e.preventDefault();
          setShowAIPrompt(true);
        }
        if (e.key === "k") {
          e.preventDefault();
          setShowShortcuts(true);
        }
        if (e.key === "ArrowUp" && selectedElement) {
          e.preventDefault();
          const index = formElements.findIndex(
            (el) => el.id === selectedElement.id,
          );
          if (index > 0) {
            const newElements = [...formElements];
            [newElements[index - 1], newElements[index]] = [
              newElements[index],
              newElements[index - 1],
            ];
            setFormElements(newElements);
            addToHistory(newElements);
          }
        }
        if (e.key === "ArrowDown" && selectedElement) {
          e.preventDefault();
          const index = formElements.findIndex(
            (el) => el.id === selectedElement.id,
          );
          if (index < formElements.length - 1) {
            const newElements = [...formElements];
            [newElements[index], newElements[index + 1]] = [
              newElements[index + 1],
              newElements[index],
            ];
            setFormElements(newElements);
            addToHistory(newElements);
          }
        }
        if (e.key === "r") {
          e.preventDefault();
          handleResetForm();
        }
      }

      if (e.key === "Delete" && selectedElement) {
        e.preventDefault();
        handleDeleteField(selectedElement.id);
      }

      if (e.key === "Escape") {
        if (showAIPrompt) setShowAIPrompt(false);
        if (showTemplates) setShowTemplates(false);
        if (showShortcuts) setShowShortcuts(false);
        if (selectedElement) setSelectedElement(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    handleUndo,
    handleRedo,
    showPreview,
    selectedElement,
    handleDeleteField,
    showAIPrompt,
    showTemplates,
    formElements,
    addToHistory,
    handleResetForm,
  ]);

  // Add handleFieldChange function
  const handleFieldChange = (elementId, value) => {
    setFormData((prev) => ({
      ...prev,
      [elementId]: value,
    }));
  };

  const renderToolbar = () => (
    <div className="flex justify-between items-center mb-6 bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-sm">
      {/* Left side */}
      <div className="flex items-center space-x-3">
        <input
          type="text"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="Enter form name"
          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Right side - grouped buttons */}
      <div className="flex items-center">
        {/* File operations group */}
        <div className="flex items-center space-x-2 px-3">
          <button
            onClick={handleResetForm}
            className="inline-flex items-center px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Reset Form
          </button>
          <button
            onClick={() => setShowTemplates(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 5h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V7a2 2 0 012-2z"
              />
            </svg>
            Templates
          </button>
          <button
            onClick={() => setShowLogoSettings(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Logo
          </button>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-gray-200 mx-3" />

        {/* View operations group */}
        <div className="flex items-center space-x-2 px-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="inline-flex items-center px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            {showPreview ? "Edit" : "Preview"}
          </button>
          <button
            onClick={() => setShowAIPrompt(true)}
            className="inline-flex items-center px-3 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            AI Generate
          </button>
        </div>

        {/* Divider */}
        <div className="h-8 w-px bg-gray-200 mx-3" />

        {/* Save operations group */}
        <div className="flex items-center space-x-2 px-3">
          <button
            onClick={() => handleSaveTemplate(false)}
            className="inline-flex items-center px-3 py-2 bg-green-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            Save & Configure Campaign
          </button>
          <button
            onClick={() => handleSaveTemplate(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            Save Draft
          </button>
        </div>
      </div>
    </div>
  );

  // Add state for logo settings modal

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (uploadedFiles.length + files.length > 10) {
      setNotification({
        show: true,
        message: "Maximum 10 files allowed",
        type: "error",
      });
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    if (uploadedFiles.length + files.length > 10) {
      setNotification({
        show: true,
        message: "Maximum 10 files allowed",
        type: "error",
      });
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Filter sidebar elements based on search
  const filteredElements = useMemo(() => {
    if (!sidebarSearch) return availableElements;

    const searchTerm = sidebarSearch.toLowerCase();
    return availableElements
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.label.toLowerCase().includes(searchTerm) ||
            item.type.toLowerCase().includes(searchTerm),
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [availableElements, sidebarSearch]);

  // Toggle category expand/collapse
  const toggleCategory = useCallback((category) => {
    setExpandedCategory((prev) => (prev === category ? null : category));
  }, []);

  // Handle adding new form elements
  const handleAddElement = useCallback(
    (element) => {
      const newElement = {
        ...element,
        id: `${element.type}-${Date.now()}`,
      };
      setFormElements((prev) => [...prev, newElement]);
      addToHistory([...formElements, newElement]);
      showNotification(`Added ${element.label} element`, "success");
    },
    [formElements, addToHistory, showNotification],
  );

  // Add state for confirmation dialogs
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  // Add the confirmation dialogs to the JSX
  <>
    {/* ... existing JSX ... */}

    <ConfirmDialog
      isOpen={showConfirmDelete}
      title="Delete Field"
      message="Are you sure you want to delete this field? This action cannot be undone."
      onConfirm={() => {
        setShowConfirmDelete(false);
      }}
      onCancel={() => setShowConfirmDelete(false)}
      type="danger"
      confirmText="Delete"
    />

    <ConfirmDialog
      isOpen={showConfirmReset}
      title="Reset Form"
      message="Are you sure you want to reset the form? This will remove all elements."
      onConfirm={() => {
        setFormElements([]);
        setSelectedElement(null);
        addToHistory([]);
        showNotification("Form has been reset successfully", "success");
        setShowConfirmReset(false);
      }}
      onCancel={() => setShowConfirmReset(false)}
      type="warning"
    />

    <ConfirmDialog
      isOpen={showConfirmRefresh}
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
      onConfirm={() => {
        window.location.reload();
      }}
      onCancel={() => setShowConfirmRefresh(false)}
      type="warning"
      confirmText="Leave"
      cancelText="Stay"
    />
  </>;

  // Add form submission handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create an object mapping database fields to their values
      const dbData = {};
      const missingRequired = [];

      formElements.forEach((element) => {
        if (element.dbField) {
          if (element.config.isComposite) {
            // Handle composite fields (like full_name)
            const compositeValue = formData[element.id] || {};
            element.config.fields.forEach((field) => {
              const value = compositeValue[field.dbField];
              if (field.required && !value) {
                missingRequired.push(`${element.label} - ${field.label}`);
              }
              if (value) {
                dbData[field.dbField] = value;
              }
            });
          } else {
            // Handle regular fields
            const value = formData[element.id];
            if (value !== undefined && value !== null && value !== "") {
              dbData[element.dbField] = value;
            }

            // Check required fields
            const dbConfig = getDbFieldConfig(element.dbField);
            if (dbConfig?.required && !value) {
              missingRequired.push(dbConfig.label);
            }
          }
        }
      });

      // Validate required fields
      if (missingRequired.length > 0) {
        throw new Error(
          `Required fields missing: ${missingRequired.join(", ")}`,
        );
      }

      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Format the data nicely for console output
      logger.group("Preview Mode - Form Submission");
      logger.info("Endpoint:", "/api/names");
      logger.info("Method:", "POST");
      logger.info("Form Data:", {
        ...dbData,
        _preview: true, // Flag to indicate this is preview mode
      });
      logger.groupEnd();

      // Show success notification with field count
      const fieldCount = Object.keys(dbData).length;
      showNotification(
        `Form validated successfully (Preview Mode) - ${fieldCount} field${fieldCount !== 1 ? "s" : ""} would be saved`,
        "success",
      );

      // Clear form values
      setFormData({});
    } catch (error) {
      logger.error("Form validation error:", error);
      setSubmitError(error.message);
      showNotification("Form validation failed: " + error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modify the preview form render to include submission logic
  const renderPreviewForm = () => {
    const sections = [];
    let currentSectionElements = [];
    let pageNumber = 1;

    // First section always uses firstSectionName
    formElements.forEach((element) => {
      if (element.type === "page-separator") {
        if (currentSectionElements.length > 0) {
          sections.push({
            elements: currentSectionElements,
            title: pageNumber === 1 ? firstSectionName : `Page ${pageNumber}`,
            description: element.config.description,
          });
          pageNumber++;
        }
        currentSectionElements = [];
      } else {
        currentSectionElements.push(element);
      }
    });

    // Add the last section if it has elements
    if (currentSectionElements.length > 0) {
      sections.push({
        elements: currentSectionElements,
        title: pageNumber === 1 ? firstSectionName : `Page ${pageNumber}`,
        description: "",
      });
    }

    // If no sections were created but we have elements, create a single section
    if (sections.length === 0 && formElements.length > 0) {
      sections.push({
        elements: formElements,
        title: firstSectionName,
        description: "",
      });
    }

    return (
      <div className="w-full max-w-3xl mx-auto p-6 bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none">
        {/* Form Title - Always displayed */}
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 text-center">
          {formName}
        </h1>

        {/* Current Section */}
        {currentSection < sections.length && (
          <div className="space-y-6">
            {/* Section Title - Only displayed if not empty */}
            {sections[currentSection].title && (
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">
                {sections[currentSection].title}
              </h2>
            )}

            {/* Section Description - Only displayed if not empty */}
            {sections[currentSection].description && (
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {sections[currentSection].description}
              </p>
            )}

            {/* Form Elements */}
            <div className="space-y-4">
              {sections[currentSection].elements.map((element) => (
                <FormElement
                  key={element.id}
                  {...element}
                  preview={true}
                  formElements={formElements}
                  onChange={(value) => handleFieldChange(element.id, value)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Navigation buttons - only show if we have multiple sections */}
        {sections.length > 1 && (
          <div className="flex justify-between pt-6 border-t mt-6">
            {currentSection > 0 && (
              <button
                type="button"
                onClick={() =>
                  setCurrentSection((prev) => Math.max(0, prev - 1))
                }
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
              >
                Previous
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                setCurrentSection((prev) =>
                  Math.min(prev + 1, sections.length - 1),
                )
              }
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors ${
                currentSection === sections.length - 1 ? "ml-auto" : ""
              }`}
            >
              {currentSection === sections.length - 1 ? "Submit" : "Next"}
            </button>
          </div>
        )}

        {/* Submit button for single-section forms */}
        {sections.length === 1 && (
          <div className="flex justify-end pt-6 border-t mt-6">
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit
            </button>
          </div>
        )}
      </div>
    );
  };

  // Add new CampaignTypeModal component before the return statement
  const CampaignTypeModal = ({ isOpen, onClose, onSave }) => {
    const [step, setStep] = useState("type"); // 'type' or 'settings'
    const [localCampaignType, setLocalCampaignType] = useState("email");
    const [localAudienceType, setLocalAudienceType] = useState("all");
    const [localEmailTemplate, setLocalEmailTemplate] = useState("default");
    const [localTokenExpiry, setLocalTokenExpiry] = useState("24");
    const [localEmbedSettings, setLocalEmbedSettings] = useState({
      buttonText: "Take Survey",
      buttonColor: "#3B82F6",
      buttonTextColor: "#FFFFFF",
      buttonFontSize: "16px",
      buttonPadding: "12px 24px",
      buttonBorderRadius: "8px",
      buttonHoverColor: "#2563EB",
      inheritStyles: false,
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
    });

    const handleSave = () => {
      onSave({
        campaignType: localCampaignType,
        audienceType: localAudienceType,
        emailTemplate: localEmailTemplate,
        tokenExpiry: localTokenExpiry,
        embedSettings: localEmbedSettings,
      });
      onClose();
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Campaign Configuration
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
            >
              ×
            </button>
          </div>

          <div className="space-y-6">
            {/* Campaign Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Campaign Type
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  className={`p-4 border rounded-lg text-left transition-all ${
                    localCampaignType === "email"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100 shadow-sm"
                      : "border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }`}
                  onClick={() => {
                    setLocalCampaignType("email");
                    setStep("settings");
                  }}
                >
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 mr-3 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <span className="font-medium">Direct Email Campaign</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 ml-11">
                    Send unique, secure links to existing clients via email
                  </p>
                </button>
                <button
                  className={`p-4 border rounded-lg text-left transition-all ${
                    localCampaignType === "embed"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100 shadow-sm"
                      : "border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }`}
                  onClick={() => {
                    setLocalCampaignType("embed");
                    setStep("settings");
                  }}
                >
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 mr-3 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                        />
                      </svg>
                    </div>
                    <span className="font-medium">Embed Link Campaign</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 ml-11">
                    Generate a public link for marketing materials and websites
                  </p>
                </button>
              </div>
            </div>

            {/* Email Campaign Settings */}
            {localCampaignType === "email" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Audience Type
                  </label>
                  <select
                    value={localAudienceType}
                    onChange={(e) => setLocalAudienceType(e.target.value)}
                    className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Clients</option>
                    <option value="pipeline">Pipeline Stage</option>
                    <option value="list">Custom List</option>
                    <option value="tags">Client Tags</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Email Template
                  </label>
                  <select
                    value={localEmailTemplate}
                    onChange={(e) => setLocalEmailTemplate(e.target.value)}
                    className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                  >
                    <option value="default">Default Template</option>
                    <option value="minimal">Minimal Template</option>
                    <option value="branded">Branded Template</option>
                    <option value="custom">Custom Template</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Link Expiry (hours)
                  </label>
                  <input
                    type="number"
                    value={localTokenExpiry}
                    onChange={(e) => setLocalTokenExpiry(e.target.value)}
                    className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                    min="1"
                    max="720"
                  />
                </div>
              </div>
            )}

            {/* Embed Campaign Settings */}
            {localCampaignType === "embed" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={localEmbedSettings.buttonText}
                      onChange={(e) =>
                        setLocalEmbedSettings((prev) => ({
                          ...prev,
                          buttonText: e.target.value,
                        }))
                      }
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Font Size
                    </label>
                    <select
                      value={localEmbedSettings.buttonFontSize}
                      onChange={(e) =>
                        setLocalEmbedSettings((prev) => ({
                          ...prev,
                          buttonFontSize: e.target.value,
                        }))
                      }
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                    >
                      <option value="14px">Small (14px)</option>
                      <option value="16px">Medium (16px)</option>
                      <option value="18px">Large (18px)</option>
                      <option value="20px">Extra Large (20px)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Button Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={localEmbedSettings.buttonColor}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            buttonColor: e.target.value,
                          }))
                        }
                        className="w-12 h-10 p-1 border rounded"
                      />
                      <input
                        type="text"
                        value={localEmbedSettings.buttonColor}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            buttonColor: e.target.value,
                          }))
                        }
                        className="flex-1 p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                        placeholder="#3B82F6"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Text Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={localEmbedSettings.buttonTextColor}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            buttonTextColor: e.target.value,
                          }))
                        }
                        className="w-12 h-10 p-1 border rounded"
                      />
                      <input
                        type="text"
                        value={localEmbedSettings.buttonTextColor}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            buttonTextColor: e.target.value,
                          }))
                        }
                        className="flex-1 p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Border Radius
                    </label>
                    <select
                      value={localEmbedSettings.buttonBorderRadius}
                      onChange={(e) =>
                        setLocalEmbedSettings((prev) => ({
                          ...prev,
                          buttonBorderRadius: e.target.value,
                        }))
                      }
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                    >
                      <option value="0px">Square</option>
                      <option value="4px">Slightly Rounded</option>
                      <option value="8px">Rounded</option>
                      <option value="16px">Very Rounded</option>
                      <option value="9999px">Pill</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Padding
                    </label>
                    <select
                      value={localEmbedSettings.buttonPadding}
                      onChange={(e) =>
                        setLocalEmbedSettings((prev) => ({
                          ...prev,
                          buttonPadding: e.target.value,
                        }))
                      }
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                    >
                      <option value="8px 16px">Compact</option>
                      <option value="12px 24px">Normal</option>
                      <option value="16px 32px">Large</option>
                      <option value="20px 40px">Extra Large</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="inheritStyles"
                    checked={localEmbedSettings.inheritStyles}
                    onChange={(e) =>
                      setLocalEmbedSettings((prev) => ({
                        ...prev,
                        inheritStyles: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="inheritStyles"
                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                  >
                    Inherit styles from website (overrides custom styling)
                  </label>
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Campaign Tracking (UTM Parameters)
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        UTM Source
                        <span
                          className="ml-1 text-sm text-gray-500 dark:text-gray-500"
                          title="Identifies where the traffic is coming from"
                        >
                          ⓘ
                        </span>
                      </label>
                      <input
                        type="text"
                        value={localEmbedSettings.utmSource}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            utmSource: e.target.value,
                          }))
                        }
                        className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                        placeholder="e.g., facebook"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        UTM Medium
                        <span
                          className="ml-1 text-sm text-gray-500 dark:text-gray-500"
                          title="Identifies the marketing medium"
                        >
                          ⓘ
                        </span>
                      </label>
                      <input
                        type="text"
                        value={localEmbedSettings.utmMedium}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            utmMedium: e.target.value,
                          }))
                        }
                        className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                        placeholder="e.g., social"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        UTM Campaign
                        <span
                          className="ml-1 text-sm text-gray-500 dark:text-gray-500"
                          title="Identifies your specific campaign"
                        >
                          ⓘ
                        </span>
                      </label>
                      <input
                        type="text"
                        value={localEmbedSettings.utmCampaign}
                        onChange={(e) =>
                          setLocalEmbedSettings((prev) => ({
                            ...prev,
                            utmCampaign: e.target.value,
                          }))
                        }
                        className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100"
                        placeholder="e.g., spring2024"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Add handleCampaignTypeSave function
  const handleCampaignTypeSave = async (settings) => {
    setCampaignType(settings.campaignType);
    setAudienceType(settings.audienceType);
    setEmailTemplate(settings.emailTemplate);
    setTokenExpiry(settings.tokenExpiry);
    setEmbedSettings(settings.embedSettings);

    // Close the campaign type modal
    setShowCampaignTypeModal(false);

    // Trigger the appropriate setup based on campaign type
    if (settings.campaignType === "email") {
      await handleEmailCampaignSetup();
    } else if (settings.campaignType === "embed") {
      handleEmbedCampaignSetup();
    }

    // Finally, save the template
    saveFinalTemplate(false);
  };

  // Add new function to handle email campaign setup
  const handleEmailCampaignSetup = async () => {
    try {
      // Generate unique UUIDs for each recipient
      const response = await fetch("/api/campaigns/generate-tokens", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          campaignId: Date.now(),
          audienceType,
          tokenExpiry,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate tokens");

      const data = await response.json();
      showNotification(
        `${data.recipientCount} unique links generated successfully!`,
        "success",
      );
    } catch (error) {
      logger.error("Error setting up email campaign:", error);
      showNotification(
        "Failed to set up email campaign. Please try again.",
        "error",
      );
    }
  };

  // Add new function to handle embed campaign setup
  const handleEmbedCampaignSetup = () => {
    const formId = Date.now();
    const embedCode = `
<!-- Form Widget -->
<div id="form-${formId}"></div>
<script src="${window.location.origin}/form-widget.js"></script>
<script>
  window.FormWidget.init({
    containerId: 'form-${formId}',
    formId: '${formId}',
    formName: '${formName}',
    buttonSettings: {
      text: '${embedSettings.buttonText}',
      color: '${embedSettings.buttonColor}',
      textColor: '${embedSettings.buttonTextColor}',
      fontSize: '${embedSettings.buttonFontSize}',
      padding: '${embedSettings.buttonPadding}',
      borderRadius: '${embedSettings.buttonBorderRadius}',
      hoverColor: '${embedSettings.buttonHoverColor}',
      inheritStyles: ${embedSettings.inheritStyles}
    },
    tracking: {
      utmSource: '${embedSettings.utmSource}',
      utmMedium: '${embedSettings.utmMedium}',
      utmCampaign: '${embedSettings.utmCampaign}'
    }
  });
</script>`;

    setEmbedCode(embedCode);
    setShowEmbedModal(true);
  };

  // Add EmbedModal component
  const EmbedModal = ({ isOpen, onClose, embedCode, campaignType }) => {
    const copyToClipboard = () => {
      navigator.clipboard.writeText(embedCode);
      showNotification("Copied to clipboard!", "success");
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="2xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {campaignType === "email" ? "Campaign Links" : "Embed Code"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            {campaignType === "email" ? (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Unique, secure links will be generated and sent to each
                  recipient. Here's a preview of how the links will look:
                </p>
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <code className="text-sm text-gray-800 dark:text-gray-200 font-mono">{embedCode}</code>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Each recipient will receive a personalized email with their
                    unique link. The links will expire after {tokenExpiry}{" "}
                    hours.
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Copy and paste this code into your website where you want the
                  form to appear:
                </p>
                <div className="relative">
                  <pre className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg text-sm overflow-x-auto text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 font-mono">
                    {embedCode}
                  </pre>
                  <button
                    onClick={copyToClipboard}
                    className="absolute top-2 right-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                  >
                    Copy
                  </button>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Preview</h4>
                  <div className="border rounded-lg p-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center">
                      <button
                        style={{
                          backgroundColor: embedSettings.buttonColor,
                          color: embedSettings.buttonTextColor,
                          fontSize: embedSettings.buttonFontSize,
                          padding: embedSettings.buttonPadding,
                          borderRadius: embedSettings.buttonBorderRadius,
                          border: "none",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                        }}
                        onMouseOver={(e) => {
                          if (!embedSettings.inheritStyles) {
                            e.target.style.backgroundColor =
                              embedSettings.buttonHoverColor;
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!embedSettings.inheritStyles) {
                            e.target.style.backgroundColor =
                              embedSettings.buttonColor;
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                          }
                        }}
                      >
                        {embedSettings.buttonText}
                      </button>
                    </div>
                  </div>
                  {embedSettings.inheritStyles && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Note: Button will inherit styles from your website
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Add firstSectionName to form settings in PropertiesPanel
  const renderPropertiesPanel = () => {
    if (!selectedElement) {
      return (
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Form Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Form Name
              </label>
              <input
                type="text"
                value={formName || ""}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter form name"
                className={baseInputStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                First Section Name
              </label>
              <input
                type="text"
                value={firstSectionName || ""}
                onChange={(e) => setFirstSectionName(e.target.value)}
                placeholder="Name for the first section"
                className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 placeholder-gray-400 dark:placeholder-gray-500"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                This will be the name of the first section of your form
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <PropertiesPanel
        element={selectedElement}
        onConfigChange={handleConfigChange}
        onDelete={() => setSelectedElement(null)}
        formElements={formElements}
        formName={formName}
        setFormName={setFormName}
        firstSectionName={firstSectionName}
        setFirstSectionName={setFirstSectionName}
      />
    );
  };

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
      <div className="p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-4 bg-white dark:bg-gray-900 rounded-full flex items-center justify-center">
          <img
            src="/images/logos/RS_logo_icon_outlined..png"
            alt="Logo"
            className="w-16 h-16 opacity-50 dark:opacity-40"
          />
        </div>
        <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
          Start Building Your Form
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6 max-w-md mx-auto">
          Drag and drop elements from the selector or use AI to generate
          a form automatically
        </p>
        <button
          onClick={() => setShowAIPrompt(true)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center mx-auto shadow-sm hover:shadow focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          <div className="w-8 h-8 mr-3 flex items-center justify-center rounded-full bg-blue-500">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          Generate with AI
        </button>
      </div>
    </div>
  );

  const renderFormBuilder = () => (
    <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="h-full p-4">
          {formElements.length === 0 ? (
            renderEmptyState()
          ) : (
            <SortableContext
              items={formElements.map((el) => el.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {formElements.map((element, index) => (
                  <SortableFormElement
                    key={element.id}
                    element={element}
                    index={index}
                    isSelected={selectedElement?.id === element.id}
                    onSelect={() => setSelectedElement(element)}
                    onConfigChange={handleConfigChange}
                    formElements={formElements}
                    setFormElements={setFormElements}
                    addToHistory={addToHistory}
                    setSelectedElement={setSelectedElement}
                    showNotification={showNotification}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </div>
      </DndContext>
    </div>
  );

  return (
    <>
      <KeyframesStyle />
      {/* Enhanced Notification System */}
      <div className={styles.notifications.container}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`${styles.notifications.toast} ${styles.notifications[notification.type]}`}
          >
            <div className={styles.notifications.icon}>{notification.icon}</div>
            <div className={styles.notifications.content}>
              <div className={styles.notifications.message}>
                {notification.message}
              </div>
            </div>
            <button
              onClick={() =>
                setNotifications((prev) =>
                  prev.filter((n) => n.id !== notification.id),
                )
              }
              className={styles.notifications.closeButton}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <SubPageLayout
        title="Campaign Builder"
        className="bg-white dark:bg-gray-900"
        subtitle="Create and manage marketing campaign forms"
        breadcrumbItems={breadcrumbItems}
      >
        <div className="max-w-7xl mx-auto px-4 py-8">
          {renderToolbar()}

          {/* Main Content */}
          <div className="grid grid-cols-12 gap-6">
            {!showPreview && (
              <div className="col-span-3">
                <div
                  className={`${styles.cards.base} p-4 sticky top-4 ${styles.shadows.md}`}
                >
                  <h3 className={`${styles.typography.h3} mb-4`}>
                    Form Elements
                  </h3>

                  {/* Add search bar */}
                  <div className="mb-6">
                    <input
                      type="text"
                      value={sidebarSearch}
                      onChange={(e) => setSidebarSearch(e.target.value)}
                      placeholder="Search elements..."
                      className={styles.sidebar.search}
                    />
                  </div>

                  {/* Form Element Categories */}
                  <div className="space-y-2">
                    {availableElements.map((category) => {
                      const isExpanded = expandedCategory === category.category;
                      const filteredItems = category.items.filter((item) =>
                        item.label
                          .toLowerCase()
                          .includes(sidebarSearch.toLowerCase()),
                      );

                      if (filteredItems.length === 0) return null;

                      return (
                        <div key={category.category}>
                          <button
                            onClick={() => toggleCategory(category.category)}
                            className={styles.sidebar.category.header}
                          >
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {category.category}
                            </span>
                            <svg
                              className={`${styles.sidebar.category.icon} ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          <div
                            className={`${styles.sidebar.category.content} ${
                              isExpanded
                                ? "max-h-[1000px] overflow-y-auto"
                                : "max-h-0 overflow-hidden"
                            } transition-all duration-300 ease-in-out`}
                          >
                            <div className="space-y-1 p-2">
                              {filteredItems.map((item) => (
                                <DraggableElement
                                  key={item.id}
                                  {...item}
                                  category={category.category}
                                  onAdd={handleAddElement}
                                  showNotification={showNotification}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div
              className={`${
                showPreview ? "col-span-12" : "col-span-6"
              } transition-all duration-200`}
            >
              {!showPreview && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <div
                    className={`min-h-[600px] rounded-xl transition-all duration-300 ${
                      isDraggingOver
                        ? "bg-blue-50 border-2 border-dashed border-blue-500"
                        : "bg-surface-default dark:bg-surface-default border border-gray-200 dark:border-gray-700"
                    } ${styles.shadows.sm}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {/* Logo */}
                    <div
                      className={`w-full flex justify-${formLogo.alignment} p-6 border-b border-gray-100 dark:border-gray-800`}
                    >
                      <img
                        src={formLogo.src}
                        alt="Form Logo"
                        style={{ width: `${formLogo.width}px` }}
                        className="h-auto"
                      />
                    </div>

                    <div className="p-6">
                      {renderFormBuilder()}
                    </div>
                  </div>
                </DndContext>
              )}

              {showPreview && renderPreviewForm()}
            </div>

            {!showPreview && (
              <div className="col-span-3">
                <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-4 sticky top-4 transition-all duration-200 hover:shadow-md dark:shadow-none">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Properties
                  </h3>
                  {renderPropertiesPanel()}
                </div>
              </div>
            )}
          </div>

          {/* AI Prompt Modal with File Upload */}
          {showAIPrompt && (
            <Modal isOpen={showAIPrompt} onClose={() => setShowAIPrompt(false)} maxWidth="2xl">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Generate Form with AI
                  </h3>
                  <button
                    onClick={() => {
                      setShowAIPrompt(false);
                      setUploadedFiles([]);
                    }}
                    className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Describe the form you want to create
                    </label>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Describe the form you want to create..."
                      rows={4}
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Upload context documents (optional, max 10 files)
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                        dragOver
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300"
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                      }}
                      onDrop={handleFileDrop}
                    >
                      <div className="text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          Drag and drop files here, or{" "}
                          <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                            browse
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleFileUpload}
                              accept=".pdf,.doc,.docx,.txt,.rtf"
                            />
                          </label>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Supported formats: PDF, DOC, DOCX, TXT, RTF
                        </p>
                      </div>

                      {/* File List */}
                      {uploadedFiles.length > 0 && (
                        <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                          {uploadedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 p-2 rounded-lg"
                            >
                              <div className="flex items-center space-x-2">
                                <svg
                                  className="w-4 h-4 text-gray-500 dark:text-gray-500"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                                  {file.name}
                                </span>
                              </div>
                              <button
                                onClick={() => removeFile(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                      {uploadedFiles.length}/10 files uploaded
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowAIPrompt(false);
                      setUploadedFiles([]);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAIGenerate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    disabled={!aiPrompt.trim()}
                  >
                    Generate
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {/* Templates Modal */}
          {showTemplates && (
            <Modal isOpen={showTemplates} onClose={() => setShowTemplates(false)} maxWidth="2xl">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Form Templates
                </h3>

                {/* Template Tabs */}
                <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setIsDraft(false)}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                        !isDraft
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                      }`}
                    >
                      Published Templates
                    </button>
                    <button
                      onClick={() => setIsDraft(true)}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                        isDraft
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                      }`}
                    >
                      Drafts
                    </button>
                  </nav>
                </div>

                {/* Template List */}
                {(isDraft ? drafts : templates).length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-500">
                    No {isDraft ? "drafts" : "templates"} saved yet
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {(isDraft ? drafts : templates).map((template) => (
                      <div
                        key={template.id}
                        className="border rounded-lg p-4 hover:border-blue-500 cursor-pointer transition-all duration-200 hover:shadow-md dark:shadow-none relative group"
                        onClick={() => handleLoadTemplate(template)}
                      >
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {template.name}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {template.elements.length} elements
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          Created{" "}
                          {new Date(template.createdAt).toLocaleDateString()}
                        </p>
                        {/* Add delete button for drafts */}
                        {isDraft && (
                          <button
                            onClick={(e) => handleDeleteDraft(template.id, e)}
                            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete draft"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setShowTemplates(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {/* Embed Code Modal */}
          {showEmbedModal && (
            <EmbedModal
              isOpen={showEmbedModal}
              onClose={() => setShowEmbedModal(false)}
              embedCode={embedCode}
              campaignType={campaignType}
            />
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidth="md">
              <div className="p-6">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Delete Draft
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                    Are you sure you want to delete this draft? This action
                    cannot be undone.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteDraft}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {/* Name Draft Modal */}
          {showNameDraftModal && (
            <Modal isOpen={showNameDraftModal} onClose={() => setShowNameDraftModal(false)} maxWidth="md">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Save Draft
                  </h3>
                  <button
                    onClick={() => setShowNameDraftModal(false)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    ×
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="draftName"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Draft Name
                    </label>
                    <input
                      type="text"
                      id="draftName"
                      value={draftNameInput}
                      onChange={(e) => setDraftNameInput(e.target.value)}
                      placeholder="Enter a name for your draft"
                      className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowNameDraftModal(false)}
                      className="px-4 py-2 bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (!draftNameInput.trim()) {
                          setNotification({
                            show: true,
                            message: "Please enter a name for your draft",
                            type: "error",
                          });
                          setTimeout(() => {
                            setNotification({
                              show: false,
                              message: "",
                              type: "",
                            });
                          }, 3000);
                          return;
                        }
                        saveFinalTemplate(true);
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                    >
                      Save Draft
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </SubPageLayout>
      <ShortcutsModal
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />
      <LogoSettingsModal
        isOpen={showLogoSettings}
        onClose={() => setShowLogoSettings(false)}
        initialLogo={formLogo}
        onSave={setFormLogo}
      />
      <ConfirmDialog
        isOpen={showConfirmReset}
        title="Reset Form"
        message="Are you sure you want to reset the form? This will remove all elements."
        onConfirm={() => {
          setFormElements([]);
          setSelectedElement(null);
          addToHistory([]);
          showNotification("Form has been reset successfully", "success");
          setShowConfirmReset(false);
        }}
        onCancel={() => setShowConfirmReset(false)}
        type="warning"
      />

      <ConfirmDialog
        isOpen={showConfirmRefresh}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost."
        onConfirm={() => {
          window.location.reload();
        }}
        onCancel={() => setShowConfirmRefresh(false)}
        type="warning"
        confirmText="Leave"
        cancelText="Stay"
      />

      {showTemplateNameModal && (
        <Modal isOpen={showTemplateNameModal} onClose={() => setShowTemplateNameModal(false)} maxWidth="md">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Name Your Form
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="templateName"
                  className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
                >
                  Form Name
                </label>
                <input
                  type="text"
                  id="templateName"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Enter a name for your form"
                  className="w-full p-2 border rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowTemplateNameModal(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!formName.trim()) {
                      showNotification(
                        "Please enter a name for your form",
                        "error",
                      );
                      return;
                    }
                    setShowTemplateNameModal(false);
                    setShowCampaignTypeModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      <CampaignTypeModal
        isOpen={showCampaignTypeModal}
        onClose={() => setShowCampaignTypeModal(false)}
        onSave={handleCampaignTypeSave}
      />
    </>
  );
};

export default Campaigns;
